/**
 * trapLure.ts — Monkey-Bomb-style enemy lure for ground-effect traps.
 *
 * BETA 20.10 — Per Dylan: Asmon's Desk Banana + Whoopie Cushion of Doom
 * become LEGENDARY. "they should act like monkeys in [CoD] zombies where it
 * lures enemies to it that are nearby. not 100 percent of enemies."
 *
 * Mirrors kittenLure.ts but scans GROUND EFFECTS (weaponState.groundEffects)
 * for the banana / whoopie trap weaponIds instead of projectiles. Each frame,
 * before enemy AI ticks, builds an assignment map:
 *   enemyId -> { x, y } — the trap the enemy should steer toward.
 *
 * For each trap:
 *   1. Find all alive enemies within LURE_RADIUS.
 *   2. Sort by distance.
 *   3. Take the closest MAX_LURED (the cap — NOT every enemy, per Dylan).
 *   4. Closest trap wins if multiple traps compete for the same enemy.
 *
 * enemySystem.steerEnemyAI consults getTrapLureTarget() and overrides the
 * steer target (same hook as the kitten lure).
 */

import type { WeaponSystemState } from './weaponSystem';

interface LureCandidateEnemy {
  id: number;
  x: number;
  y: number;
  alive: boolean;
}

// Trap weaponIds that lure. Matches the ground-effect weaponId set in engine.ts.
const TRAP_WEAPON_IDS = new Set<string>([
  'item_asmons_desk_banana',
  'item_whoopie_cushion_of_doom',
]);

// Monkey-bomb tuning. Radius = how far the trap pulls. MAX_LURED = the cap
// (not 100% of nearby enemies — per Dylan). Generous for a legendary.
const LURE_RADIUS = 200;
const MAX_LURED = 7;

const _assignment = new Map<number, { x: number; y: number }>();

/** Per-frame entry point. Called from updateGame BEFORE enemy AI ticks. */
export function tickTrapLure(weaponState: WeaponSystemState | null, enemies: readonly LureCandidateEnemy[]): void {
  _assignment.clear();
  if (!weaponState) return;

  // Collect alive trap ground effects.
  const traps: { x: number; y: number }[] = [];
  for (const ge of weaponState.groundEffects) {
    if (ge.lifetime <= 0) continue;
    if (!TRAP_WEAPON_IDS.has(ge.weaponId)) continue;
    traps.push({ x: ge.x, y: ge.y });
  }
  if (traps.length === 0) return;

  const radiusSq = LURE_RADIUS * LURE_RADIUS;
  const bestDist = new Map<number, number>();

  for (const trap of traps) {
    const candidates: { id: number; d2: number }[] = [];
    for (const e of enemies) {
      if (!e.alive) continue;
      const dx = e.x - trap.x;
      const dy = e.y - trap.y;
      const d2 = dx * dx + dy * dy;
      if (d2 >= radiusSq) continue;
      candidates.push({ id: e.id, d2 });
    }
    if (candidates.length === 0) continue;
    candidates.sort((a, b) => a.d2 - b.d2);
    const limit = Math.min(MAX_LURED, candidates.length);
    for (let i = 0; i < limit; i++) {
      const c = candidates[i];
      const prev = bestDist.get(c.id);
      if (prev === undefined || c.d2 < prev) {
        bestDist.set(c.id, c.d2);
        _assignment.set(c.id, { x: trap.x, y: trap.y });
      }
    }
  }
}

/** Query: does this enemy currently have a trap lure target? */
export function getTrapLureTarget(enemyId: number): { x: number; y: number } | null {
  return _assignment.get(enemyId) ?? null;
}

export function getActiveTrapLuredCount(): number {
  return _assignment.size;
}
